/**
 * Toast — single toast system for the whole app.
 * ──────────────────────────────────────────────────────────────────────────
 * Every toast (useToast, showSuccess, handleApiError) renders through your
 * `showToast` component (react-native-toast-message), shown TOP-CENTER.
 *
 * `ToastProvider` + `useToast` are kept as a thin compatibility layer so the
 * ~19 existing `const toast = useToast()` call sites work unchanged — their
 * methods now just delegate to `showToast`. The old custom bottom-right
 * renderer has been removed.
 *
 * API (unchanged):
 *   const toast = useToast();
 *   toast.success(msg, opts?)   toast.error(msg, opts?)
 *   toast.warn(msg, opts?)      toast.info(msg, opts?)
 *   toast(...)  // callable: ({ tone, title, message })
 *   toast.dismiss()
 */

import React from 'react';
import { showToast } from './toastComponent';

export type ToastTone = 'success' | 'warn' | 'danger' | 'info';

export interface ToastOptions {
  tone?: ToastTone;
  title?: string;
  message?: string;
  duration?: number;
}

export interface ToastItem extends ToastOptions {
  id: string;
}

export interface ToastApi {
  (opts: ToastOptions | string): string;
  success: (msg: string, opts?: Omit<ToastOptions, 'tone'>) => string;
  error:   (msg: string, opts?: Omit<ToastOptions, 'tone'>) => string;
  warn:    (msg: string, opts?: Omit<ToastOptions, 'tone'>) => string;
  info:    (msg: string, opts?: Omit<ToastOptions, 'tone'>) => string;
  dismiss: (id?: string) => void;
}

// react-native-toast-message has 'success' | 'error' | 'info' types; map our
// tones onto them (warn → error styling, as a cautionary message).
function toType(tone?: ToastTone): 'success' | 'error' | 'info' {
  if (tone === 'danger' || tone === 'warn') return 'error';
  if (tone === 'info') return 'info';
  return 'success';
}

function fire(toneOrType: 'success' | 'error' | 'info', msg: string, sub?: string): string {
  // text1 = headline, text2 = optional detail line.
  showToast(toneOrType as 'success' | 'error', msg, sub);
  return '';
}

// The shared api object — identical surface to the old provider.
const api = ((opts: ToastOptions | string): string => {
  if (typeof opts === 'string') return fire('success', opts);
  const text1 = opts.title ?? opts.message ?? '';
  const text2 = opts.title ? opts.message : undefined;
  return fire(toType(opts.tone), text1, text2);
}) as ToastApi;

api.success = (msg, opts) => fire('success', msg, opts?.message);
api.error   = (msg, opts) => fire('error', msg, opts?.message);
api.warn    = (msg, opts) => fire('error', msg, opts?.message);
api.info    = (msg, opts) => fire('info', msg, opts?.message);
api.dismiss = () => { /* react-native-toast-message auto-hides; no-op */ };

/** Provider is now a passthrough — the <Toast/> root lives in App.tsx. */
export function ToastProvider({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}

export function useToast(): ToastApi {
  return api;
}

export default ToastProvider;
